<template>
  <div class="header">
    <!-- 左侧区域 -->
    <div class="header-left">
      <el-button 
        type="text" 
        @click="$emit('toggle-sidebar')"
        class="sidebar-toggle"
      >
        <el-icon><Fold v-if="!collapsed" /><Expand v-else /></el-icon>
      </el-button>
    </div>
    
    <!-- 右侧区域 -->
    <div class="header-right">
      <!-- 消息通知 -->
      <el-badge :value="messageCount" class="message-badge">
        <el-button type="text" @click="showMessages">
          <el-icon><Bell /></el-icon>
        </el-button>
      </el-badge>
      
      <!-- 全屏切换 -->
      <el-button type="text" @click="toggleFullscreen">
        <el-icon><FullScreen /></el-icon>
      </el-button>
      
      <!-- 用户信息 -->
      <el-dropdown @command="handleUserCommand">
        <div class="user-info">
          <el-avatar :src="userInfo.avatar" :size="32">
            {{ userInfo.name?.charAt(0) }}
          </el-avatar>
          <span class="user-name">{{ userInfo.name }}</span>
          <el-icon><ArrowDown /></el-icon>
        </div>
        
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="profile">个人中心</el-dropdown-item>
            <el-dropdown-item command="settings">系统设置</el-dropdown-item>
            <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  Fold, 
  Expand, 
  Bell, 
  FullScreen, 
  ArrowDown 
} from '@element-plus/icons-vue'

interface UserInfo {
  name: string
  avatar?: string
  role: string
}

interface Props {
  collapsed: boolean
  userInfo: UserInfo
}

interface Emits {
  (e: 'toggle-sidebar'): void
  (e: 'user-action', action: string): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const router = useRouter()
const messageCount = ref(3)

const showMessages = () => {
  ElMessage.info('消息功能开发中...')
}

const toggleFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen()
  } else {
    document.documentElement.requestFullscreen()
  }
}

const handleUserCommand = (command: string) => {
  switch (command) {
    case 'profile':
      router.push('/personal-center')
      break
    case 'settings':
      ElMessage.info('系统设置功能开发中...')
      break
    case 'logout':
      handleLogout()
      break
  }
  emit('user-action', command)
}

const handleLogout = async () => {
  try {
    // 调用后端登出API
    const { logout } = await import('@/api/auth')
    await logout()
  } catch (error) {
    console.warn('后端登出失败，继续执行前端登出:', error)
  }
  
  // 清除前端用户状态
  const { useUserStore } = await import('@/stores/user')
  const userStore = useUserStore()
  userStore.clearUserInfo()
  
  // 清除localStorage
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  
  // 清除权限缓存
  const { clearPermissions } = await import('@/utils/permission')
  clearPermissions()
  
  ElMessage.success('退出登录成功')
  router.push('/login')
}
</script>

<style lang="scss" scoped>
.header {
  height: 60px;
  background: white;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  margin-bottom: 0;
  
  .header-left {
    .sidebar-toggle {
      font-size: 18px;
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    
    .message-badge {
      .el-button {
        font-size: 18px;
      }
    }
    
    .user-info {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 4px;
      transition: background-color 0.3s;
      
      &:hover {
        background-color: #f5f5f5;
      }
      
      .user-name {
        font-size: 14px;
        color: #333;
      }
    }
  }
}
</style>